import { memo, type ReactNode, useCallback, useEffect, useId, useMemo, useState } from 'react';

import { type WorkspaceAgentTableMonikerSegment } from '~/components/v2/components/workspace/workspace-agent-panel/components/table-moniker/WorkspaceAgentTableCellContent';
import { WorkspaceAgentTablePillFooter } from '~/components/v2/components/workspace/workspace-agent-panel/components/table-moniker/WorkspaceAgentTablePillFooter';
import { WorkspaceAgentTablePillScrollport } from '~/components/v2/components/workspace/workspace-agent-panel/components/table-moniker/WorkspaceAgentTablePillScrollport';
import { type TablePill } from '~/components/v2/components/workspace/workspace-agent-panel/schemas/pillMetadata';
import { type WorkspaceAgentTableRow } from '~/components/v2/components/workspace/workspace-agent-panel/schemas/workspaceAgentTableArtifact';
import { shouldLoadMoreWorkspaceAgentTableRows } from '~/components/v2/components/workspace/workspace-agent-panel/utils/shouldLoadMoreWorkspaceAgentTableRows';

export const WORKSPACE_AGENT_TABLE_PILL_PAGE_SIZE = 5;

export type WorkspaceAgentTablePillBodyFrameProps = {
  children: ReactNode;
  visibleRows: readonly WorkspaceAgentTableRow[];
};

export type WorkspaceAgentTablePillProps = {
  'data-testid'?: string;
  /** More rows exist on the server than `pill.rows` holds; `onLoadMore` fetches the next batch. */
  hasMore?: boolean;
  isFetchNextPageError?: boolean;
  isLoadingMore?: boolean;
  onLoadMore?: () => void;
  onRetryLoadMore?: () => void;
  pageSize?: number;
  pill: TablePill;
  /**
   * Wraps the scrollport. The private chip uses this to resolve monikers on `visibleRows` only.
   * Absent, the table renders its body directly.
   */
  renderBodyFrame?: (props: WorkspaceAgentTablePillBodyFrameProps) => ReactNode;
  /** Draws a moniker inside a cell. Without it the moniker shows as text. */
  renderMoniker?: (segment: WorkspaceAgentTableMonikerSegment) => ReactNode;
};

/**
 * A flat, read-only table. Paging is client-side over the rows loaded so far; reaching the last
 * loaded page asks `onLoadMore` for the next batch, so the arrows run to the end of the artifact.
 */
export const WorkspaceAgentTablePill = memo(
  ({
    'data-testid': testId = 'WORKSPACE_AGENT_TABLE_PILL',
    hasMore = false,
    isFetchNextPageError = false,
    isLoadingMore = false,
    onLoadMore,
    onRetryLoadMore,
    pageSize = WORKSPACE_AGENT_TABLE_PILL_PAGE_SIZE,
    pill,
    renderBodyFrame,
    renderMoniker,
  }: WorkspaceAgentTablePillProps) => {
    const captionId = useId();
    const [page, setPage] = useState(0);
    const pageCount = Math.max(1, Math.ceil(pill.rows.length / pageSize));
    const currentPage = Math.min(page, pageCount - 1);

    useEffect(() => {
      setPage((previous) => Math.min(previous, pageCount - 1));
    }, [pageCount]);

    const atLastLoadedPage = currentPage >= pageCount - 1;

    useEffect(() => {
      if (
        onLoadMore &&
        shouldLoadMoreWorkspaceAgentTableRows({
          atLastLoadedPage,
          canLoadMore: true,
          hasMore,
          isLoadingMore,
        })
      ) {
        onLoadMore();
      }
    }, [atLastLoadedPage, hasMore, isLoadingMore, onLoadMore]);

    const visibleRows = useMemo(
      () => pill.rows.slice(currentPage * pageSize, (currentPage + 1) * pageSize),
      [currentPage, pageSize, pill.rows],
    );

    const handlePreviousPage = useCallback(() => {
      if (currentPage === 0) {
        return;
      }

      setPage(currentPage - 1);
    }, [currentPage]);

    const handleNextPage = useCallback(() => {
      if (currentPage >= pageCount - 1) {
        return;
      }

      setPage(currentPage + 1);
    }, [currentPage, pageCount]);

    const isPreviousPageDisabled = currentPage === 0;
    const isNextPageDisabled = atLastLoadedPage;

    const firstShown = currentPage * pageSize + 1;
    const lastShown = currentPage * pageSize + visibleRows.length;
    const total = Math.max(pill.count, pill.rows.length);
    const rangeLabel = `${firstShown}\u2013${lastShown} of ${total}`;
    const showFooter = pageCount > 1 || total > pill.rows.length || hasMore;

    const loadingMoreLabel = isLoadingMore ? ' (loading more…)' : '';
    const fetchErrorLabel = isFetchNextPageError ? ' (could not load more — retry)' : '';
    const scrollport = (
      <WorkspaceAgentTablePillScrollport
        captionId={showFooter ? captionId : undefined}
        currentPage={currentPage}
        pill={pill}
        renderMoniker={renderMoniker}
        visibleRows={visibleRows}
      />
    );
    const tableBody = renderBodyFrame ? renderBodyFrame({ children: scrollport, visibleRows }) : scrollport;

    return (
      <div
        className="w-full min-w-0 max-w-full rounded-6 ads:bg-raised bg-gray-2 py-2 ads:ring-subtle ring-1 ring-gray-3 ring-inset"
        data-testid={testId}
      >
        {pill.title ? (
          <p
            className="mb-2 line-clamp-2 break-words px-2.5 font-medium ads:text-secondary text-12 text-gray-11 leading-4"
            data-testid="WORKSPACE_AGENT_TABLE_PILL_TITLE"
            title={pill.title}
          >
            {pill.title}
          </p>
        ) : null}
        {tableBody}
        {showFooter ? (
          <WorkspaceAgentTablePillFooter
            captionId={captionId}
            fetchErrorLabel={fetchErrorLabel}
            hasMore={hasMore}
            isFetchNextPageError={isFetchNextPageError}
            isNextPageDisabled={isNextPageDisabled}
            isPreviousPageDisabled={isPreviousPageDisabled}
            loadingMoreLabel={loadingMoreLabel}
            onNextPage={handleNextPage}
            onPreviousPage={handlePreviousPage}
            onRetryLoadMore={onRetryLoadMore}
            pageCount={pageCount}
            rangeLabel={rangeLabel}
          />
        ) : null}
      </div>
    );
  },
);

WorkspaceAgentTablePill.displayName = 'WorkspaceAgentTablePill';
